import HttpService from "./http-service";

import ResponseInformationRequest, {
  ReponseInformationRequestAttachment,
} from "@/entities/response-information-request";
import AsyncPaginatedRequest from "./async-paginated-request";
import { LazyAsyncIter } from "@/iterators";
import { pageMapperBuilder, Paginated } from "@/pagination";
import { SnakeCasedProperties, SnakeCasedPropertiesDeep } from "type-fest";

/**
 *  Serviced to CRUD Citizen Information Requests1`
 */
export default class ResponseInformationRequestService extends HttpService {
  public getAllCitizenInformationRequest(
    status?: 0 | 1 | null,
    type_request?: 1 | 2 | null
  ): AsyncIterator<Paginated<ResponseInformationRequest>> {
    const params = {
      status,
      type_request,
    };
    const asyncReq = new AsyncPaginatedRequest<ResponseInformationRequestDTO>(
      "get",
      "citizen-information-request",
      params,
      undefined,
      this.httpClient
    )[Symbol.asyncIterator]();

    return new LazyAsyncIter(asyncReq).map(
      pageMapperBuilder(ResponseInformationRequestFromDTO)
    );
  }

  /**
   * Get information request
   */

  public async getInformationRequest(
    requestId: number
  ): Promise<ResponseInformationRequest> {
    const { data } = await this.httpClient.get<ResponseInformationRequestDTO>(
      `citizen-information-request/${requestId}`
    );
    return ResponseInformationRequestFromDTO(data);
  }

  /**
   *  Create a new information request
   */
  public async insertResponseInformationRequest(
    request: CreateResponseInformationRequest
  ): Promise<number> {
    let reqData: CreateResponseInformationRequestDTO | FormData = {
      ...request,
    };
    // If we have new attachments convert request data to FormData
    if (request.newAttachments) {
      reqData = this.createFormData(request);
    }
    const { data } = await this.httpClient.post(
      `/citizen-info-req-res`,
      reqData,
      this.setHeaders(reqData)
    );
    return data;
  }

  /**
   * Update an information request
   */

  public async updateInformationRequest(
    request: UpdateResponseInformationRequest
  ): Promise<void> {
    let reqData: UpdateResponseInformationRequestDTO | FormData = {
      id: request.id,
      description: request.description,
      status_id: request.status_id,
      citizen_request_id: request.citizen_request_id,
      remove_attachments: undefined,
      new_attachments: undefined,
    };

    reqData = this.createFormData(request) as FormData;
    // PHP doesn't parse Form Data when the request method is not POST. We use method
    // spoofing to hit the PUT route with POST
    let method: "put" | "post" = "put";
    if (reqData instanceof FormData) {
      reqData.append("_method", "PUT");
      method = "post";
    }

    await this.httpClient[method](
      `/citizen-info-req-res/${request.id}`,
      reqData,
      this.setHeaders(reqData)
    );
  }

  /**
   * Delete an information request
   */

  public async deleteInformationRequest(informationId: number): Promise<void> {
    await this.httpClient.delete(`/citizen-info-req-res/${informationId}`);
  }

  private setHeaders(
    data: CreateResponseUpdateInformationRequestDTO
  ): { headers: Record<string, string> } | undefined {
    if (data instanceof FormData) {
      return {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      };
    }
  }

  private createFormData(
    request: UpdateResponseInformationRequest | CreateResponseInformationRequest
  ): CreateResponseUpdateInformationRequestDTO {
    const reqData:
      | CreateResponseInformationRequestDTO
      | UpdateResponseInformationRequestDTO = {
      // id: request.id,
      description: request.description,
      status_id: request.status_id,
      citizen_request_id: request.citizen_request_id,
      new_attachments: undefined,
      remove_attachments: (request as UpdateResponseInformationRequest)
        .removeAttachments,
    };

    // If we have new attachments, convert request data to FormData
    if (request.newAttachments) {
      reqData.new_attachments = request.newAttachments;
      const formData = new FormData();
      Object.entries(reqData).forEach(([name, value]) => {
        if (value !== undefined) {
          if (Array.isArray(value)) {
            if (name === "new_attachments") {
              (value as File[]).forEach((file) =>
                formData.append("new_attachments[]", file)
              );
            } else if (name === "remove_attachments") {
              (value as number[]).forEach((id) =>
                formData.append("remove_attachments[]", id.toString())
              );
            }
          } else {
            formData.append(name, value?.toString());
          }
        }
      });
      return formData;
    }
    return reqData;
  }
}

export type UpdateResponseInformationRequest = Omit<
  ResponseInformationRequest,
  "attachments"
> & {
  /**
   * Ids of attachments to delete.
   */
  removeAttachments?: number[];
  newAttachments?: File[];
};

export type CreateResponseInformationRequest = Omit<
  UpdateResponseInformationRequest,
  "id" | "removeAttachments"
>;
type UpdateResponseInformationRequestDTO =
  SnakeCasedProperties<UpdateResponseInformationRequest>;
type CreateResponseInformationRequestDTO =
  SnakeCasedProperties<CreateResponseInformationRequest>;
type CreateResponseUpdateInformationRequestDTO =
  | UpdateResponseInformationRequestDTO
  | FormData
  | CreateResponseInformationRequestDTO;

export type ResponseInformationRequestDTO =
  SnakeCasedPropertiesDeep<ResponseInformationRequest>;
export type ResponseInformationRequestAttachmentDTO =
  SnakeCasedProperties<ReponseInformationRequestAttachment>;

export function ResponseInformationRequestFromDTO(
  dto: ResponseInformationRequestDTO
): ResponseInformationRequest {
  return new ResponseInformationRequest({
    id: dto.id,
    description: dto.description,
    status_id: dto.status_id,
    citizen_request_id: dto.citizen_request_id,
    attachments: dto.attachments.map(
      ResponseInformationRequestAttachmentFromDTO
    ),
  });
}

export function ResponseInformationRequestAttachmentFromDTO(
  dto: ResponseInformationRequestAttachmentDTO
): ReponseInformationRequestAttachment {
  return {
    id: dto.id,
    name: dto.name,
    download_url: dto.download_url,
  };
}
